import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Table,
  Input,
  Button,
  Space,
  Tag,
  Typography,
  Modal,
  Form,
  Select,
  message,
  Tabs,
  Card,
  Row,
  Col,
  Popconfirm,
  Tooltip,
  DatePicker,
  Empty,
  Spin,
  Dropdown,
  Statistic,
  Drawer,
  List,
  Avatar,
  Timeline,
  Descriptions,
  Alert,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  MinusOutlined,
  CloseOutlined,
  ToolOutlined,
  SendOutlined,
  PaperClipOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  ExportOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../config/api';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { getTrackingInfo } from '../utils/trackingDeepLink';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const REPLACEMENT_STATUS_OPTIONS = [
  { value: 'new', label: 'New', textColor: '#1677ff' },
  { value: 'in_progress', label: 'In Progress', textColor: '#1677ff' },
  { value: 'completed', label: 'Solved', textColor: '#389e0d' },
];

const getReplacementStatusLabel = (status: string) =>
  REPLACEMENT_STATUS_OPTIONS.find((s) => s.value === status)?.label || status;

const CarrierLogo: React.FC<{ carrier: string | null; label: string | null }> = ({
  carrier,
  label,
}) => {
  if (carrier === 'fedex') {
    return (
      <span
        aria-label="FedEx"
        title="FedEx"
        style={{ display: 'inline-flex', alignItems: 'center', fontSize: 17, fontWeight: 800, letterSpacing: -1 }}
      >
        <span style={{ color: '#4D148C' }}>Fed</span>
        <span style={{ color: '#FF6600' }}>Ex</span>
      </span>
    );
  }

  if (carrier === 'ups') {
    return (
      <svg
        aria-label="UPS"
        role="img"
        viewBox="0 0 42 48"
        width="25"
        height="29"
        style={{ display: 'block' }}
      >
        <title>UPS</title>
        <path
          d="M21 1.5 39 8v15.2c0 11-7.2 19-18 23.3C10.2 42.2 3 34.2 3 23.2V8l18-6.5Z"
          fill="#351C15"
          stroke="#FFB500"
          strokeWidth="2.5"
        />
        <text
          x="21"
          y="29"
          fill="#FFB500"
          fontFamily="Arial, sans-serif"
          fontSize="13"
          fontWeight="700"
          textAnchor="middle"
        >
          UPS
        </text>
      </svg>
    );
  }

  return label ? <Tag>{label}</Tag> : null;
};

const formatUserName = (first?: string, last?: string) =>
  [first, last].filter(Boolean).join(' ') || '—';

const formatBusinessAddress = (record: {
  business_address?: string;
  business_city?: string;
  business_state?: string;
  business_zip_code?: string;
}) => {
  const parts = [
    record.business_address,
    record.business_city,
    record.business_state,
    record.business_zip_code,
  ].filter((x) => x && String(x).trim());
  return parts.length ? parts.join(', ') : null;
};

const MAX_ATTACHMENT_SIZE = 25 * 1024 * 1024;
const workspaceSectionPadding = '16px 20px';

const filterValidFiles = (files: File[]) => {
  const valid = files.filter((file) => {
    const isValidType = file.type.startsWith('image/') || file.type === 'application/pdf';
    const isValidSize = file.size <= MAX_ATTACHMENT_SIZE;
    return isValidType && isValidSize;
  });
  if (valid.length !== files.length) {
    message.error('Only images and PDFs under 25MB are allowed');
  }
  return valid;
};

interface ModelDashboardStats {
  model: string;
  total: number;
}

interface ReplacementDashboardStats {
  month?: string;
  start_date?: string;
  end_date?: string;
  by_model: ModelDashboardStats[];
}

type DashboardTimeframe =
  | 'this_month'
  | 'last_month'
  | 'last_7_days'
  | 'last_30_days'
  | 'last_90_days'
  | 'last_6_months'
  | 'last_12_months'
  | 'this_year'
  | 'last_year'
  | 'all_time'
  | 'custom';

const DASHBOARD_TIMEFRAME_OPTIONS: { value: DashboardTimeframe; label: string }[] = [
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_90_days', label: 'Last 90 Days' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'last_12_months', label: 'Last 12 Months' },
  { value: 'this_year', label: 'This Year' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'all_time', label: 'All Time' },
  { value: 'custom', label: 'Custom' },
];

// Earliest date the dashboard looks back to for "All Time"; predates any replacement record.
const DASHBOARD_ALL_TIME_START = '2000-01-01';

const getDashboardDateRange = (
  timeframe: DashboardTimeframe,
  customRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
) => {
  const now = dayjs();
  switch (timeframe) {
    case 'last_month': {
      const lastMonth = now.subtract(1, 'month');
      return { start: lastMonth.startOf('month'), end: lastMonth.endOf('month') };
    }
    case 'last_7_days':
      return { start: now.subtract(6, 'day').startOf('day'), end: now.endOf('day') };
    case 'last_30_days':
      return { start: now.subtract(29, 'day').startOf('day'), end: now.endOf('day') };
    case 'last_90_days':
      return { start: now.subtract(89, 'day').startOf('day'), end: now.endOf('day') };
    case 'last_6_months':
      return { start: now.subtract(6, 'month').add(1, 'day').startOf('day'), end: now.endOf('day') };
    case 'last_12_months':
      return { start: now.subtract(12, 'month').add(1, 'day').startOf('day'), end: now.endOf('day') };
    case 'this_year':
      return { start: now.startOf('year'), end: now.endOf('year') };
    case 'last_year': {
      const lastYear = now.subtract(1, 'year');
      return { start: lastYear.startOf('year'), end: lastYear.endOf('year') };
    }
    case 'all_time':
      return { start: dayjs(DASHBOARD_ALL_TIME_START).startOf('day'), end: now.endOf('day') };
    case 'custom':
      if (customRange?.[0] && customRange?.[1]) {
        return { start: customRange[0].startOf('day'), end: customRange[1].endOf('day') };
      }
      return { start: now.startOf('month'), end: now.endOf('month') };
    case 'this_month':
    default:
      return { start: now.startOf('month'), end: now.endOf('month') };
  }
};

const formatDashboardPeriod = (start?: string, end?: string) => {
  if (!start || !end) return '';
  return `${dayjs(start).format('MMM D, YYYY')} – ${dayjs(end).format('MMM D, YYYY')}`;
};

interface EquipmentType {
  value: string;
  label: string;
}

interface EquipmentRecord {
  id: string;
  equipment_type: string;
  model: string;
  serial_number?: string;
  notes?: string;
  status: string;
  location?: string;
  created_at: string;
  updated_at: string;
  assigned_businesses: any[];
}

interface Replacement {
  id: string;
  business_id: string;
  business_name?: string;
  mid?: string;
  business_address?: string;
  business_city?: string;
  business_state?: string;
  business_zip_code?: string;
  business_email?: string;
  business_phone?: string;
  equipment_type: string;
  model: string;
  status?: string;
  replaced_by?: string;
  replaced_by_user_id?: string;
  replaced_by_first_name?: string;
  replaced_by_last_name?: string;
  replacement_date: string;
  serial_number?: string;
  serial_initial?: string;
  tracking_number?: string;
  ticket_id?: string;
  ticket_number?: string;
  notes?: string;
  created_by?: string;
  created_by_first_name?: string;
  created_by_last_name?: string;
  created_at: string;
  updated_at: string;
  last_comment?: string;
  last_comment_at?: string;
}

interface ReplacementAttachment {
  id: string;
  filename: string;
  original_filename: string;
  mime_type: string;
  file_size: number;
  comment_id?: string | null;
  uploaded_at: string;
  uploaded_by_first_name?: string;
  uploaded_by_last_name?: string;
}

interface ReplacementEmailPreview {
  subject: string;
  to: string;
  business_name: string;
}

interface ReplacementComment {
  id: string;
  comment: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  created_at: string;
  attachments?: ReplacementAttachment[];
}

interface ActivityEvent {
  type: string;
  label: string;
  occurred_at?: string;
  detail?: {
    actor?: string | null;
    change?: string | null;
    action?: string;
    entity_type?: string;
  };
}

interface Business {
  id: string;
  business_name: string;
  mid?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

const Equipment: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const canManageReplacements = usePermission('manage_replacements');
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [typeForm] = Form.useForm();
  const [replacementForm] = Form.useForm();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [typeModalVisible, setTypeModalVisible] = useState(false);
  const [replacementModalVisible, setReplacementModalVisible] = useState(false);
  const [replacementModalMinimized, setReplacementModalMinimized] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [, setEditingType] = useState<EquipmentType | null>(null);
  const [editingReplacementId, setEditingReplacementId] = useState<string | null>(null);
  const [editingReplacementRecord, setEditingReplacementRecord] = useState<Replacement | null>(null);
  const [businessSearchTerm, setBusinessSearchTerm] = useState('');
  const [ticketSearchTerm, setTicketSearchTerm] = useState('');
  const [selectedEquipmentType, setSelectedEquipmentType] = useState<string | undefined>(undefined);
  const [replacementDateRange, setReplacementDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [replacementSearchInput, setReplacementSearchInput] = useState('');
  const [replacementSearch, setReplacementSearch] = useState('');
  const [dashboardTimeframe, setDashboardTimeframe] = useState<DashboardTimeframe>('this_month');
  const [dashboardDateRange, setDashboardDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [dashboardReplacementsModal, setDashboardReplacementsModal] = useState<{
    visible: boolean;
    model: string;
  } | null>(null);
  // This page is also embedded in Settings > Equipment, where the URL is not /equipment.
  // Embedded usage keeps the equipment inventory tab and never navigates away from Settings.
  const isEquipmentRoute = location.pathname === '/equipment';

  const getInitialTab = () => {
    if (!isEquipmentRoute) return 'equipment';
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const tabParam = params.get('tab');
    if (
      tabParam === 'types'
      || tabParam === 'equipment'
      || tabParam === 'replacements'
      || tabParam === 'dashboard'
    ) {
      return tabParam;
    }
    return canManageReplacements ? 'dashboard' : 'equipment';
  };

  const [activeTab, setActiveTab] = useState<string>(getInitialTab);
  const [equipmentPagination, setEquipmentPagination] = useState({ current: 1, pageSize: 10 });
  const [replacementsPagination, setReplacementsPagination] = useState({ current: 1, pageSize: 10 });
  const [dashboardReplacementsPagination, setDashboardReplacementsPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const commentFileInputRef = useRef<HTMLInputElement>(null);
  const [commentsDrawerVisible, setCommentsDrawerVisible] = useState(false);
  const [selectedReplacement, setSelectedReplacement] = useState<Replacement | null>(null);
  const [newComment, setNewComment] = useState('');
  const [commentSelectedFiles, setCommentSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<ReplacementAttachment | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [replacementStatusFilter, setReplacementStatusFilter] = useState<string | undefined>();
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [emailReplacementForSend, setEmailReplacementForSend] = useState<Replacement | null>(null);
  const [emailPreview, setEmailPreview] = useState<ReplacementEmailPreview | null>(null);
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');

  // Fetch equipment
  const { data: equipmentData, isLoading } = useQuery(
    ['equipment', searchTerm],
    async () => {
      const response = await api.get('/api/equipment', {
        params: { search: searchTerm || undefined }
      });
      return response.data;
    }
  );

  const equipment = equipmentData?.equipment || [];

  // Fetch equipment types
  const { data: typesData, isLoading: typesLoading } = useQuery(
    'equipment-types',
    async () => {
      const response = await api.get('/api/equipment/types');
      return response.data;
    }
  );

  const equipmentTypes = typesData?.types || [];

  // State to store the current business being edited
  const [currentBusiness, setCurrentBusiness] = useState<Business | null>(null);

  // Debounced so typing a customer name does not fire a request per keystroke
  const [businessSearchQuery, setBusinessSearchQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setBusinessSearchQuery(businessSearchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [businessSearchTerm]);

  // Fetch businesses for replacement form
  const { data: businessesData, isLoading: businessesLoading } = useQuery(
    ['businesses-search', businessSearchQuery],
    async () => {
      const response = await api.get('/api/businesses/search', {
        params: { search: businessSearchQuery, limit: 50 }
      });
      return response.data;
    },
    {
      enabled: businessSearchQuery.length >= 2,
      keepPreviousData: true,
    }
  );
  const businessSearchPending = businessSearchTerm.trim() !== businessSearchQuery;

  // Fetch tickets for replacement form (searchable by ticket number/subject)
  const { data: replacementTicketsData, isLoading: replacementTicketsLoading } = useQuery(
    ['tickets-search', ticketSearchTerm],
    async () => {
      const response = await api.get('/api/tickets', {
        params: { search: ticketSearchTerm, limit: 30, page: 1 }
      });
      return response.data?.tickets || [];
    },
    { enabled: ticketSearchTerm.length >= 1 }
  );
  // When editing, ensure current ticket is in options if not from search
  const replacementTicketsOptions = (() => {
    const list = replacementTicketsData || [];
    const record = editingReplacementRecord;
    const ticketId = record?.ticket_id;
    const ticketNumber = record?.ticket_number;
    if (ticketId && ticketNumber && !list.some((t: any) => t.id === ticketId)) {
      return [{ id: ticketId, ticket_number: ticketNumber, subject: '' }, ...list];
    }
    return list;
  })();

  // Fetch agents/admins for "Who Replaced it" dropdown
  const { data: agentsData } = useQuery(
    'users-agents',
    async () => {
      const response = await api.get('/api/users/agents');
      return response.data?.users || [];
    }
  );
  const agentsList = useMemo(() => {
    const list = [...(agentsData || [])];
    if (user?.id && !list.some((a: any) => String(a.id) === String(user.id))) {
      list.unshift({
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        role: user.role,
      });
    }
    return list;
  }, [agentsData, user]);

  const businesses = businessesData?.businesses || [];
  
  // Include current business in the list if it exists
  const allBusinesses = currentBusiness && !businesses.find((b: Business) => b.id === currentBusiness.id)
    ? [currentBusiness, ...businesses]
    : businesses;

  // Fetch equipment models based on selected equipment type
  const { data: equipmentModelsData, isLoading: modelsLoading } = useQuery(
    ['equipment-models', selectedEquipmentType],
    async () => {
      if (!selectedEquipmentType) {
        return { models: [] };
      }
      const response = await api.get('/api/equipment', {
        params: { equipment_type: selectedEquipmentType }
      });
      // Get unique models for the selected equipment type
      const equipment = response.data?.equipment || [];
      const uniqueModels = Array.from(new Set(equipment.map((eq: EquipmentRecord) => eq.model).filter(Boolean)));
      return { models: uniqueModels.sort() };
    },
    {
      enabled: !!selectedEquipmentType,
    }
  );

  const equipmentModels = equipmentModelsData?.models || [];

  useEffect(() => {
    const timer = setTimeout(() => setReplacementSearch(replacementSearchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [replacementSearchInput]);

  // Filters shrink the result set, so an out-of-range page would render an empty table
  useEffect(() => {
    setReplacementsPagination((prev) => (prev.current === 1 ? prev : { ...prev, current: 1 }));
  }, [replacementDateRange, replacementStatusFilter, replacementSearch]);

  // Fetch replacements with date, status and customer filters
  const { data: replacementsData, isLoading: replacementsLoading } = useQuery(
    ['equipment-replacements', replacementDateRange, replacementStatusFilter, replacementSearch],
    async () => {
      const params: any = {};
      if (replacementDateRange && replacementDateRange[0] && replacementDateRange[1]) {
        params.start_date = replacementDateRange[0].format('YYYY-MM-DD');
        params.end_date = replacementDateRange[1].format('YYYY-MM-DD');
      }
      if (replacementStatusFilter) {
        params.status = replacementStatusFilter;
      }
      if (replacementSearch) {
        params.search = replacementSearch;
      }
      const response = await api.get('/api/equipment/replacements', { params });
      return response.data;
    },
    { keepPreviousData: true }
  );

  const replacements = replacementsData?.replacements || [];

  const {
    data: commentsData,
    isLoading: commentsLoading,
    refetch: refetchComments,
  } = useQuery(
    ['equipment-replacement-comments', selectedReplacement?.id],
    async () => {
      const response = await api.get(`/api/equipment/replacements/${selectedReplacement!.id}/comments`);
      return response.data;
    },
    { enabled: !!selectedReplacement?.id && commentsDrawerVisible }
  );
  const comments: ReplacementComment[] = commentsData?.comments || [];
  const taskAttachments: ReplacementAttachment[] = commentsData?.task_attachments || [];

  const {
    data: activityData,
    isLoading: activityLoading,
    refetch: refetchActivity,
  } = useQuery(
    ['equipment-replacement-activity', selectedReplacement?.id],
    async () => {
      const response = await api.get(`/api/equipment/replacements/${selectedReplacement!.id}/activity`);
      return response.data;
    },
    { enabled: !!selectedReplacement?.id && commentsDrawerVisible }
  );
  const activityEvents: ActivityEvent[] = activityData?.events || [];

  const dashboardDateBounds = useMemo(
    () => getDashboardDateRange(dashboardTimeframe, dashboardDateRange),
    [dashboardTimeframe, dashboardDateRange]
  );
  // replacement_date is a calendar date, so send plain dates to keep the bounds inclusive
  const dashboardStartParam = dashboardDateBounds.start.format('YYYY-MM-DD');
  const dashboardEndParam = dashboardDateBounds.end.format('YYYY-MM-DD');

  const { data: dashboardData, isLoading: dashboardLoading } = useQuery(
    ['equipment-replacements-dashboard-stats', dashboardStartParam, dashboardEndParam],
    async () => {
      const response = await api.get('/api/equipment/replacements/dashboard-stats', {
        params: {
          start_date: dashboardStartParam,
          end_date: dashboardEndParam,
        },
      });
      return response.data;
    },
    { enabled: canManageReplacements, keepPreviousData: true }
  );
  const dashboardStats: ReplacementDashboardStats | undefined = dashboardData?.stats;
  const dashboardByModel: ModelDashboardStats[] = dashboardStats?.by_model || [];

  const { data: dashboardReplacementsData, isLoading: dashboardReplacementsLoading } = useQuery(
    [
      'equipment-replacements-dashboard-list',
      dashboardReplacementsModal?.model,
      dashboardStartParam,
      dashboardEndParam,
    ],
    async () => {
      const response = await api.get('/api/equipment/replacements', {
        params: {
          model: dashboardReplacementsModal!.model,
          start_date: dashboardStartParam,
          end_date: dashboardEndParam,
        },
      });
      return response.data;
    },
    { enabled: !!dashboardReplacementsModal?.visible }
  );
  const dashboardReplacements: Replacement[] = dashboardReplacementsData?.replacements || [];

  // Create equipment mutation
  const createEquipmentMutation = useMutation(
    async (data: any) => {
      const response = await api.post('/api/equipment', data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['equipment']);
        setModalVisible(false);
        form.resetFields();
        message.success('Equipment created successfully');
      },
    }
  );

  // Update equipment mutation
  const updateEquipmentMutation = useMutation(
    async ({ id, data }: { id: string; data: any }) => {
      const response = await api.put(`/api/equipment/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['equipment']);
        setModalVisible(false);
        setEditingId(null);
        form.resetFields();
        message.success('Equipment updated successfully');
      },
    }
  );

  // Delete equipment mutation
  const deleteEquipmentMutation = useMutation(
    async (id: string) => {
      await api.delete(`/api/equipment/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['equipment']);
        message.success('Equipment deleted successfully');
      },
    }
  );

  // Create equipment type mutation
  const createTypeMutation = useMutation(
    async (data: { value: string; label: string }) => {
      const response = await api.post('/api/equipment/types', data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('equipment-types');
        setTypeModalVisible(false);
        typeForm.resetFields();
        message.success('Equipment type created successfully');
      },
    }
  );

  // Delete equipment type mutation
  const deleteTypeMutation = useMutation(
    async (value: string) => {
      await api.delete(`/api/equipment/types/${value}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('equipment-types');
        message.success('Equipment type deleted successfully');
      },
    }
  );

  const uploadReplacementAttachments = async (replacementId: string, files: File[]) => {
    setUploadingFiles(true);
    try {
      const ids: string[] = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('attachment', file);
        const response = await api.post(
          `/api/equipment/replacements/${replacementId}/attachments`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        if (response.data?.attachment?.id) {
          ids.push(response.data.attachment.id);
        }
      }
      return ids;
    } finally {
      setUploadingFiles(false);
    }
  };

  // Create replacement mutation
  const createReplacementMutation = useMutation(
    async (data: any) => {
      const payload: any = {
        business_id: data.business_id,
        equipment_type: data.equipment_type,
        model: data.model,
        replacement_date: dayjs(data.replacement_date).format('YYYY-MM-DD'),
        serial_initial: data.serial_initial || undefined,
        serial_number: data.serial_number || undefined,
        tracking_number: data.tracking_number || undefined,
        ticket_id: data.ticket_id || undefined,
        replaced_by_user_id: data.replaced_by_user_id || undefined,
        notes: data.notes || undefined,
        status: data.status || 'new',
      };
      const response = await api.post('/api/equipment/replacements', payload);
      return response.data;
    },
    {
      onSuccess: (data: any) => {
        queryClient.invalidateQueries('equipment-replacements');
        queryClient.invalidateQueries('equipment-replacements-dashboard-stats');
        setReplacementModalVisible(false);
        setReplacementModalMinimized(false);
        replacementForm.resetFields();
        setBusinessSearchTerm('');
        setTicketSearchTerm('');
        setSelectedEquipmentType(undefined);
        setCurrentBusiness(null);
        setEditingReplacementId(null);
        setEditingReplacementRecord(null);
        const ticketNum = data?.replacement?.ticket_number;
        message.success(ticketNum ? `Replacement created. Ticket #${ticketNum} created.` : 'Replacement created successfully');
        if (data?.replacement) {
          setSelectedReplacement(data.replacement);
          setNewComment('');
          setCommentSelectedFiles([]);
          setCommentsDrawerVisible(true);
        }
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to create replacement');
      },
    }
  );

  // Update replacement mutation
  const updateReplacementMutation = useMutation(
    async ({ id, data }: { id: string; data: any }) => {
      const formattedData = { ...data };
      if (formattedData.replacement_date) {
        if (dayjs.isDayjs(formattedData.replacement_date)) {
          formattedData.replacement_date = formattedData.replacement_date.format('YYYY-MM-DD');
        } else if (typeof formattedData.replacement_date === 'string') {
          formattedData.replacement_date = dayjs(formattedData.replacement_date).format('YYYY-MM-DD');
        }
      }
      const response = await api.put(`/api/equipment/replacements/${id}`, formattedData);
      return response.data;
    },
    {
      onSuccess: (data: any) => {
        queryClient.invalidateQueries('equipment-replacements');
        queryClient.invalidateQueries('equipment-replacements-dashboard-stats');
        queryClient.invalidateQueries(['equipment-replacement-activity']);
        setReplacementModalVisible(false);
        setEditingReplacementId(null);
        setEditingReplacementRecord(null);
        replacementForm.resetFields();
        setBusinessSearchTerm('');
        if (data?.replacement) {
          setSelectedReplacement((prev) =>
            prev?.id === data.replacement.id ? { ...prev, ...data.replacement } : prev
          );
        }
        message.success('Replacement updated successfully');
      },
      onError: (error: any) => {
        console.error('Update replacement error:', error);
        const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to update replacement';
        message.error(errorMessage);
      },
    }
  );

  const updateReplacementStatusMutation = useMutation(
    async ({ id, status }: { id: string; status: string }) => {
      const response = await api.put(`/api/equipment/replacements/${id}`, { status });
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('equipment-replacements');
        queryClient.invalidateQueries('equipment-replacements-dashboard-stats');
        queryClient.invalidateQueries(['equipment-replacement-activity']);
        if (data?.replacement) {
          setSelectedReplacement((prev) =>
            prev?.id === data.replacement.id ? { ...prev, ...data.replacement } : prev
          );
        }
        message.success(`Status changed to ${getReplacementStatusLabel(data?.replacement?.status || '')}`);
      },
      onError: (err: any) => {
        message.error(err?.response?.data?.error || 'Failed to change status');
      },
    }
  );

  const sendReplacementEmailMutation = useMutation(
    async ({ replacementId, message: emailBody }: { replacementId: string; message: string }) => {
      const response = await api.post(`/api/equipment/replacements/${replacementId}/send-email`, {
        message: emailBody,
      });
      return response.data;
    },
    {
      onSuccess: (data, variables) => {
        message.success(`Email sent to ${data.sent_to}`);
        queryClient.invalidateQueries(['equipment-replacement-activity', variables.replacementId]);
        refetchActivity();
        setEmailModalVisible(false);
        setEmailReplacementForSend(null);
        setEmailPreview(null);
        setEmailMessage('');
      },
      onError: (err: any) => {
        message.error(err?.response?.data?.error || 'Failed to send email');
      },
    }
  );

  // Delete replacement mutation
  const deleteReplacementMutation = useMutation(
    async (id: string) => {
      await api.delete(`/api/equipment/replacements/${id}`);
    },
    {
      onSuccess: (_data, id) => {
        queryClient.invalidateQueries('equipment-replacements');
        queryClient.invalidateQueries('equipment-replacements-dashboard-stats');
        if (selectedReplacement?.id === id) {
          setCommentsDrawerVisible(false);
          setSelectedReplacement(null);
        }
        message.success('Replacement deleted successfully');
      },
    }
  );

  const addCommentMutation = useMutation(
    async ({ comment, attachmentIds }: { comment: string; attachmentIds: string[] }) => {
      const response = await api.post(`/api/equipment/replacements/${selectedReplacement!.id}/comments`, {
        comment,
        attachment_ids: attachmentIds,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        setNewComment('');
        setCommentSelectedFiles([]);
        refetchComments();
        refetchActivity();
        queryClient.invalidateQueries('equipment-replacements');
        message.success('Comment added');
      },
      onError: () => {
        message.error('Failed to add comment');
      },
    }
  );

  const deleteCommentMutation = useMutation(
    async (commentId: string) => {
      await api.delete(`/api/equipment/replacements/comments/${commentId}`);
    },
    {
      onSuccess: () => {
        refetchComments();
        refetchActivity();
        message.success('Comment deleted');
      },
      onError: () => {
        message.error('Failed to delete comment');
      },
    }
  );

  const handleSubmit = (values: any) => {
    if (editingId) {
      updateEquipmentMutation.mutate({ id: editingId, data: values });
    } else {
      createEquipmentMutation.mutate(values);
    }
  };

  const handleTypeSubmit = (values: { value: string; label: string }) => {
    createTypeMutation.mutate(values);
  };

  const handleEdit = (record: EquipmentRecord) => {
    setEditingId(record.id);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = (id: string) => {
    deleteEquipmentMutation.mutate(id);
  };

  const handleDeleteType = (value: string) => {
    deleteTypeMutation.mutate(value);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingId(null);
    form.resetFields();
  };

  const handleTypeModalClose = () => {
    setTypeModalVisible(false);
    setEditingType(null);
    typeForm.resetFields();
  };

  const handleReplacementSubmit = (values: any) => {
    if (editingReplacementId) {
      updateReplacementMutation.mutate({ id: editingReplacementId, data: values });
    } else {
      createReplacementMutation.mutate(values);
    }
  };

  const handleEditReplacement = async (record: Replacement) => {
    setEditingReplacementId(record.id);
    setSelectedEquipmentType(record.equipment_type);
    
    // Fetch business details if we have business_id
    if (record.business_id) {
      try {
        const response = await api.get(`/api/businesses/${record.business_id}`);
        const business = response.data.business;
        setCurrentBusiness(business);
        // Set search term to show the business name
        setBusinessSearchTerm(business.business_name || '');
      } catch (error) {
        console.error('Failed to fetch business:', error);
        // If fetch fails, try to use the business_name from the record if available
        if (record.business_name) {
          setBusinessSearchTerm(record.business_name);
        }
      }
    }
    
    replacementForm.setFieldsValue({
      ...record,
      status: record.status || 'new',
      replacement_date: dayjs(record.replacement_date),
      ticket_id: record.ticket_id || undefined,
      replaced_by_user_id: record.replaced_by_user_id || undefined,
    });
    if (record.ticket_number) setTicketSearchTerm(record.ticket_number);
    setEditingReplacementRecord(record);
    setReplacementModalVisible(true);
    setReplacementModalMinimized(false);
  };

  const handleDeleteReplacement = (id: string) => {
    deleteReplacementMutation.mutate(id);
  };

  const handleReplacementModalClose = () => {
    setReplacementModalVisible(false);
    setReplacementModalMinimized(false);
    setEditingReplacementId(null);
    setEditingReplacementRecord(null);
    replacementForm.resetFields();
    setBusinessSearchTerm('');
    setTicketSearchTerm('');
    setSelectedEquipmentType(undefined);
    setCurrentBusiness(null);
  };

  const handleReplacementMinimize = () => {
    setReplacementModalMinimized(true);
  };

  const handleReplacementRestore = () => {
    setReplacementModalMinimized(false);
  };

  const openReplacementDrawer = (record: Replacement) => {
    setSelectedReplacement(record);
    setNewComment('');
    setCommentSelectedFiles([]);
    setCommentsDrawerVisible(true);
  };

  const handleCloseReplacementDrawer = () => {
    setCommentsDrawerVisible(false);
    setSelectedReplacement(null);
    setNewComment('');
    setCommentSelectedFiles([]);
  };

  const handleQuickStatusChange = (record: Replacement, status: string) => {
    if (record.status === status) return;
    updateReplacementStatusMutation.mutate({ id: record.id, status });
  };

  const handleOpenSendEmail = async (record: Replacement) => {
    setEmailPreviewLoading(true);
    setEmailReplacementForSend(record);
    setEmailMessage('');
    setEmailModalVisible(true);
    try {
      const response = await api.get(`/api/equipment/replacements/${record.id}/email-preview`);
      setEmailPreview(response.data.preview);
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Failed to load email preview');
      setEmailModalVisible(false);
      setEmailReplacementForSend(null);
      setEmailPreview(null);
      setEmailMessage('');
    } finally {
      setEmailPreviewLoading(false);
    }
  };

  const handleCloseEmailModal = () => {
    setEmailModalVisible(false);
    setEmailReplacementForSend(null);
    setEmailPreview(null);
    setEmailPreviewLoading(false);
    setEmailMessage('');
  };

  const handleCreateReplacement = () => {
    setEditingReplacementId(null);
    setEditingReplacementRecord(null);
    setCurrentBusiness(null);
    setBusinessSearchTerm('');
    setTicketSearchTerm('');
    setSelectedEquipmentType(undefined);
    replacementForm.resetFields();
    replacementForm.setFieldsValue({
      status: 'new',
      replaced_by_user_id: user?.id,
      replacement_date: dayjs(),
    });
    setReplacementModalVisible(true);
    setReplacementModalMinimized(false);
  };

  const handleSendComment = async () => {
    if (!selectedReplacement) return;
    const commentText = newComment.trim();
    if (!commentText && commentSelectedFiles.length === 0) return;
    try {
      let attachmentIds: string[] = [];
      if (commentSelectedFiles.length > 0) {
        attachmentIds = await uploadReplacementAttachments(selectedReplacement.id, commentSelectedFiles);
      }
      await addCommentMutation.mutateAsync({ comment: commentText, attachmentIds });
    } catch {
      message.error('Failed to send comment');
    }
  };

  const handleCommentFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = filterValidFiles(Array.from(event.target.files || []));
    if (files.length) {
      setCommentSelectedFiles((prev) => [...prev, ...files]);
    }
    event.target.value = '';
  };

  const openAttachmentPreview = async (attachment: ReplacementAttachment) => {
    setPreviewAttachment(attachment);
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      const response = await api.get(
        `/api/equipment/replacements/attachments/${attachment.filename}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(response.data);
      setPreviewUrl(url);
    } catch {
      message.error('Failed to load attachment');
      setPreviewAttachment(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closeAttachmentPreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewAttachment(null);
  };

  const renderSelectedFileTags = (files: File[], onRemove: (index: number) => void) => {
    if (!files.length) return null;
    return (
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #E7E0EC', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {files.map((file, index) => (
          <Tag
            key={`${file.name}-${index}`}
            closable
            onClose={() => onRemove(index)}
            icon={file.type === 'application/pdf' ? <FilePdfOutlined /> : <FileImageOutlined />}
          >
            {file.name}
          </Tag>
        ))}
      </div>
    );
  };

  const renderAttachmentTags = (attachments: ReplacementAttachment[], withMargin = true) => {
    if (!attachments.length) return null;
    return (
      <div style={{ marginTop: withMargin ? 8 : 0, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {attachments.map((att) => (
          <Tag
            key={att.id}
            style={{ cursor: 'pointer' }}
            icon={att.mime_type?.startsWith('image/') ? <FileImageOutlined /> : <FilePdfOutlined />}
            onClick={() => openAttachmentPreview(att)}
          >
            {att.original_filename}
          </Tag>
        ))}
      </div>
    );
  };

  const renderActivityEventContent = (evt: ActivityEvent) => (
    <div>
      <Text style={{ fontSize: 13 }}>{evt.label}</Text>
      <div>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {[
            evt.occurred_at ? dayjs(evt.occurred_at).format('MMM D, YYYY h:mm A') : null,
            evt.detail?.actor,
            evt.detail?.change,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
      </div>
    </div>
  );

  const buildActivityTimelineItems = (events: ActivityEvent[]) =>
    [...events].reverse().map((evt, idx) => ({
      key: `${evt.type}-${evt.occurred_at}-${idx}`,
      children: renderActivityEventContent(evt),
    }));

  const renderTrackingPanel = (trackingNumber?: string) => {
    if (!trackingNumber) {
      return <Text type="secondary">No tracking number</Text>;
    }
    const info = getTrackingInfo(trackingNumber);
    const href = info.url || info.fallback_url;
    return (
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <div>
          <Text code>{trackingNumber}</Text>
          {info.carrier_label && (
            <span style={{ display: 'inline-flex', marginLeft: 8, verticalAlign: 'middle' }}>
              <CarrierLogo carrier={info.carrier} label={info.carrier_label} />
            </span>
          )}
        </div>
        {href && (
          <Button
            type="primary"
            size="small"
            icon={<ExportOutlined />}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
          >
            Track shipment
          </Button>
        )}
        {!info.carrier && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Carrier not detected — opens a search for this tracking number.
          </Text>
        )}
      </Space>
    );
  };

  const getExportData = () => {
    // Filter replacements by date range if set
    let dataToExport = replacements;
    if (replacementDateRange && replacementDateRange[0] && replacementDateRange[1]) {
      dataToExport = replacements.filter((r: Replacement) => {
        const replacementDate = dayjs(r.replacement_date);
        return replacementDate.isAfter(replacementDateRange[0]!.subtract(1, 'day')) && 
               replacementDate.isBefore(replacementDateRange[1]!.add(1, 'day'));
      });
    }

    // Prepare data for export
    const exportData = dataToExport.map((r: Replacement) => {
      const replacedByName = (r.replaced_by_first_name != null || r.replaced_by_last_name != null)
        ? [r.replaced_by_first_name, r.replaced_by_last_name].filter(Boolean).join(' ')
        : r.replaced_by || '';
      return {
        'Business Name': r.business_name || 'N/A',
        'MID': r.mid || '',
        'Ticket #': r.ticket_number ? `#${r.ticket_number}` : '',
        'Status': getReplacementStatusLabel(r.status || 'new'),
        'Equipment Type': r.equipment_type || '',
        'Model': r.model || '',
        'Replaced By': replacedByName,
        'Replacement Date': r.replacement_date ? dayjs(r.replacement_date).format('YYYY-MM-DD') : '',
        'Serial (Initial)': r.serial_initial || '',
        'Serial (Replacement)': r.serial_number || '',
        'Tracking #': r.tracking_number || '',
        'Notes': r.notes || '',
        'Created By': r.created_by_first_name && r.created_by_last_name 
          ? `${r.created_by_first_name} ${r.created_by_last_name}` 
          : '',
        'Created At': r.created_at ? dayjs(r.created_at).format('YYYY-MM-DD HH:mm:ss') : ''
      };
    });

    return exportData;
  };

  const handleExportCSV = () => {
    const exportData = getExportData();
    
    if (exportData.length === 0) {
      message.warning('No data to export');
      return;
    }

    const headers = [
      'Business Name',
      'MID',
      'Ticket #',
      'Status',
      'Equipment Type',
      'Model',
      'Replaced By',
      'Replacement Date',
      'Serial (Initial)',
      'Serial (Replacement)',
      'Tracking #',
      'Notes',
      'Created By',
      'Created At',
    ];
    const csvRows = [
      headers.join(','),
      ...exportData.map((row: any) => {
        return headers.map((h) => `"${String(row[h] || '').replace(/"/g, '""')}"`).join(',');
      })
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    const dateRangeStr = replacementDateRange && replacementDateRange[0] && replacementDateRange[1]
      ? `_${replacementDateRange[0].format('YYYY-MM-DD')}_to_${replacementDateRange[1].format('YYYY-MM-DD')}`
      : '_all';
    link.setAttribute('download', `equipment_replacements${dateRangeStr}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    message.success(`Exported ${exportData.length} replacement(s) to CSV`);
  };

  const handleExportExcel = () => {
    const exportData = getExportData();
    
    if (exportData.length === 0) {
      message.warning('No data to export');
      return;
    }

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Set column widths
    const colWidths = [
      { wch: 20 }, // Business Name
      { wch: 15 }, // MID
      { wch: 18 }, // Equipment Type
      { wch: 20 }, // Model
      { wch: 18 }, // Replaced By
      { wch: 18 }, // Replacement Date
      { wch: 18 }, // Serial Number
      { wch: 30 }, // Notes
      { wch: 18 }, // Created By
      { wch: 20 }  // Created At
    ];
    ws['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(wb, ws, 'Equipment Replacements');
    
    // Generate file name
    const dateRangeStr = replacementDateRange && replacementDateRange[0] && replacementDateRange[1]
      ? `_${replacementDateRange[0].format('YYYY-MM-DD')}_to_${replacementDateRange[1].format('YYYY-MM-DD')}`
      : '_all';
    
    // Write file
    XLSX.writeFile(wb, `equipment_replacements${dateRangeStr}.xlsx`);
    
    message.success(`Exported ${exportData.length} replacement(s) to Excel`);
  };

  const handleExportReplacements = (format: 'csv' | 'excel') => {
    if (format === 'csv') {
      handleExportCSV();
    } else {
      handleExportExcel();
    }
  };

  const equipmentColumns: ColumnsType<EquipmentRecord> = [
    {
      title: 'Type',
      dataIndex: 'equipment_type',
      key: 'equipment_type',
      render: (type: string) => (
        <Tag color="blue">{type}</Tag>
      ),
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const getStatusDisplay = (status: string) => {
          switch (status) {
            case 'active':
              return 'Active';
            case 'not_supported':
              return 'Not Supported';
            default:
              return status;
          }
        };
        
        return (
          <Tag color={status === 'active' ? 'green' : 'red'}>
            {getStatusDisplay(status)}
          </Tag>
        );
      },
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
    },
    ...(canManageReplacements
      ? [
          {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, record: EquipmentRecord) => (
              <Space>
                <Tooltip title="Edit">
                  <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                </Tooltip>
                <Popconfirm
                  title="Are you sure you want to delete this equipment?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Tooltip title="Delete">
                    <Button type="text" danger icon={<DeleteOutlined />} />
                  </Tooltip>
                </Popconfirm>
              </Space>
            ),
          } as ColumnsType<EquipmentRecord>[number],
        ]
      : []),
  ];

  const typeColumns: ColumnsType<EquipmentType> = [
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
    },
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
    },
    ...(isAdmin
      ? [
          {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, record: EquipmentType) => (
              <Space>
                <Popconfirm
                  title="Are you sure you want to delete this equipment type?"
                  onConfirm={() => handleDeleteType(record.value)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Tooltip title="Delete">
                    <Button type="text" danger icon={<DeleteOutlined />} />
                  </Tooltip>
                </Popconfirm>
              </Space>
            ),
          } as ColumnsType<EquipmentType>[number],
        ]
      : []),
  ];

  const replacementColumns: ColumnsType<Replacement> = [
    {
      title: 'Business',
      dataIndex: 'business_name',
      key: 'business_name',
      render: (text: string) => text || 'N/A',
    },
    {
      title: 'Ticket #',
      dataIndex: 'ticket_number',
      key: 'ticket_number',
      render: (text: string | number, record: Replacement) => {
        if (!text) return '—';
        const ticketNumber = String(text);
        return (
          <a
            href={`/workspace/number/${ticketNumber}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(`/workspace/number/${ticketNumber}`, {
                state: { openInNewTab: true },
              });
            }}
          >
            #{ticketNumber}
          </a>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: string, record: Replacement) => (
        <Select
          size="small"
          value={status || 'new'}
          onClick={(e) => e.stopPropagation()}
          onChange={(value) => handleQuickStatusChange(record, value)}
          loading={
            updateReplacementStatusMutation.isLoading
            && updateReplacementStatusMutation.variables?.id === record.id
          }
          style={{ minWidth: 120 }}
        >
          {REPLACEMENT_STATUS_OPTIONS.map((option) => (
            <Option key={option.value} value={option.value}>
              <span style={{ color: option.textColor, fontWeight: 500 }}>{option.label}</span>
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Equipment Type',
      dataIndex: 'equipment_type',
      key: 'equipment_type',
      render: (type: string) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
    },
    {
      title: 'Replaced By',
      key: 'replaced_by',
      render: (_: unknown, record: Replacement) => {
        if (record.replaced_by_first_name != null || record.replaced_by_last_name != null) {
          return [record.replaced_by_first_name, record.replaced_by_last_name].filter(Boolean).join(' ') || '—';
        }
        return record.replaced_by || '—';
      },
    },
    {
      title: 'Date',
      dataIndex: 'replacement_date',
      key: 'replacement_date',
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
    },
    {
      title: 'Serial (Init)',
      dataIndex: 'serial_initial',
      key: 'serial_initial',
      width: 90,
      render: (text: string) => (
        <span style={{ wordBreak: 'break-word', whiteSpace: 'normal', display: 'block', fontSize: 12 }}>
          {text || '—'}
        </span>
      ),
    },
    {
      title: 'Serial (Repl)',
      dataIndex: 'serial_number',
      key: 'serial_number',
      width: 90,
      render: (text: string) => (
        <span style={{ wordBreak: 'break-word', whiteSpace: 'normal', display: 'block', fontSize: 12 }}>
          {text || '—'}
        </span>
      ),
    },
    {
      title: 'Tracking #',
      dataIndex: 'tracking_number',
      key: 'tracking_number',
      render: (text: string) => {
        if (!text) return '—';
        const info = getTrackingInfo(text);
        const href = info.url || info.fallback_url;
        return (
          <Space size={4} wrap>
            {href ? (
              <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                {text}
              </a>
            ) : (
              text
            )}
            {info.carrier_label && (
              <CarrierLogo carrier={info.carrier} label={info.carrier_label} />
            )}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditReplacement(record)}
            />
          </Tooltip>
          {canManageReplacements && (
            <Popconfirm
              title="Are you sure you want to delete this replacement?"
              onConfirm={() => handleDeleteReplacement(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Delete">
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (!isEquipmentRoute) return;
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'replacements' || tabParam === 'dashboard') {
      if (!authLoading && !canManageReplacements) {
        setActiveTab('equipment');
        navigate('/equipment?tab=equipment', { replace: true });
        return;
      }
      setActiveTab(tabParam);
      return;
    }
    if (tabParam === 'types' || tabParam === 'equipment') {
      setActiveTab(tabParam);
    } else if (!tabParam && canManageReplacements && !authLoading) {
      setActiveTab('dashboard');
    } else {
      setActiveTab('equipment');
    }
  }, [location.search, canManageReplacements, authLoading, navigate, isEquipmentRoute]);

  useEffect(() => {
    if (!isEquipmentRoute || authLoading || !canManageReplacements) return;
    const params = new URLSearchParams(location.search);
    const replacementId = params.get('replacementId');
    if (!replacementId) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await api.get(`/api/equipment/replacements/${replacementId}`);
        const replacement = response.data?.replacement;
        if (!cancelled && replacement) {
          setActiveTab('replacements');
          openReplacementDrawer(replacement);
          navigate('/equipment?tab=replacements', { replace: true });
        }
      } catch (error) {
        console.error('Failed to open replacement from search:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.search, canManageReplacements, authLoading, navigate, isEquipmentRoute]);

  const handleTabChange = (key: string) => {
    if ((key === 'replacements' || key === 'dashboard') && !canManageReplacements) return;
    const allowedTabs = [...(canManageReplacements ? ['dashboard', 'replacements'] : []), 'equipment', 'types'];
    if (!allowedTabs.includes(key)) return;
    if (!isEquipmentRoute) {
      setActiveTab(key);
      return;
    }
    navigate(`/equipment?tab=${key}`);
  };

  const handleOpenDashboardReplacementsModal = (item: ModelDashboardStats) => {
    if (item.total <= 0) return;
    setDashboardReplacementsModal({
      visible: true,
      model: item.model,
    });
  };

  return (
    <div style={{ padding: 24, maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}>
      <Title level={2}>Replacement Management</Title>
      
      <Tabs activeKey={activeTab} onChange={handleTabChange}>
        {canManageReplacements && (
        <TabPane tab="Dashboard" key="dashboard">
          <Card>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[16, 16]}>
              <Col>
                <Title level={4} style={{ margin: 0 }}>Dashboard</Title>
                <Text type="secondary">
                  {formatDashboardPeriod(
                    dashboardStats?.start_date || dashboardDateBounds.start.toISOString(),
                    dashboardStats?.end_date || dashboardDateBounds.end.toISOString()
                  )}
                </Text>
              </Col>
              <Col>
                <Space wrap>
                  <Select
                    value={dashboardTimeframe}
                    onChange={(value: DashboardTimeframe) => setDashboardTimeframe(value)}
                    style={{ width: 180 }}
                  >
                    {DASHBOARD_TIMEFRAME_OPTIONS.map((option) => (
                      <Option key={option.value} value={option.value}>{option.label}</Option>
                    ))}
                  </Select>
                  {dashboardTimeframe === 'custom' && (
                    <RangePicker
                      placeholder={['Start Date', 'End Date']}
                      format="YYYY-MM-DD"
                      value={dashboardDateRange}
                      onChange={(dates) =>
                        setDashboardDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null)
                      }
                    />
                  )}
                </Space>
              </Col>
            </Row>
            {dashboardLoading ? (
              <div style={{ textAlign: 'center', padding: 48 }}>
                <Spin size="large" />
              </div>
            ) : dashboardByModel.length === 0 ? (
              <Text type="secondary">No equipment models configured.</Text>
            ) : (
              <Row gutter={[16, 16]}>
                {dashboardByModel.map((item) => {
                  const isEmpty = item.total === 0;
                  return (
                    <Col xs={24} sm={12} lg={8} xl={6} key={item.model}>
                      <Card
                        size="small"
                        style={{
                          height: '100%',
                          opacity: isEmpty ? 0.75 : 1,
                          borderColor: isEmpty ? '#f0f0f0' : undefined,
                        }}
                      >
                        <Text strong style={{ display: 'block', marginBottom: 12, fontSize: 15 }}>
                          {item.model}
                        </Text>
                        <div
                          role={item.total > 0 ? 'button' : undefined}
                          tabIndex={item.total > 0 ? 0 : undefined}
                          style={{
                            cursor: item.total > 0 ? 'pointer' : 'default',
                            borderRadius: 8,
                            padding: item.total > 0 ? '4px 0' : undefined,
                          }}
                          onClick={() => handleOpenDashboardReplacementsModal(item)}
                          onKeyDown={(e) => {
                            if (item.total > 0 && (e.key === 'Enter' || e.key === ' ')) {
                              e.preventDefault();
                              handleOpenDashboardReplacementsModal(item);
                            }
                          }}
                        >
                          <Statistic
                            title="Replacements"
                            value={item.total}
                            prefix={<ToolOutlined />}
                            valueStyle={{ color: isEmpty ? undefined : '#1890ff', fontSize: 24 }}
                          />
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Card>
        </TabPane>
        )}
        {canManageReplacements && (
        <TabPane tab="Replacements" key="replacements">
          <Card>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} wrap>
              <Col xs={24} sm={24} md={12} lg={8}>
                <Title level={4}>Equipment Replacements</Title>
              </Col>
              <Col xs={24} sm={24} md={12} lg={16}>
                <Space wrap>
                  <Input
                    placeholder="Search customer, MID, serial, tracking..."
                    prefix={<SearchOutlined />}
                    value={replacementSearchInput}
                    onChange={(e) => setReplacementSearchInput(e.target.value)}
                    style={{ width: 280, minWidth: 220 }}
                    allowClear
                  />
                  <DatePicker.RangePicker
                    placeholder={['Start Date', 'End Date']}
                    value={replacementDateRange}
                    onChange={(dates) => setReplacementDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null)}
                    format="YYYY-MM-DD"
                    style={{ width: 280, minWidth: 240 }}
                    allowClear
                  />
                  <Select
                    allowClear
                    placeholder="Status"
                    value={replacementStatusFilter}
                    onChange={(value) => setReplacementStatusFilter(value)}
                    style={{ width: 140 }}
                  >
                    {REPLACEMENT_STATUS_OPTIONS.map((s) => (
                      <Option key={s.value} value={s.value}>{s.label}</Option>
                    ))}
                  </Select>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'csv',
                          label: 'Export as CSV',
                          icon: <DownloadOutlined />,
                          onClick: () => handleExportReplacements('csv'),
                        },
                        {
                          key: 'excel',
                          label: 'Export as Excel',
                          icon: <DownloadOutlined />,
                          onClick: () => handleExportReplacements('excel'),
                        },
                      ],
                    }}
                  >
                    <Button icon={<DownloadOutlined />} disabled={replacements.length === 0}>
                      Export
                    </Button>
                  </Dropdown>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreateReplacement}
                  >
                    Add Replacement
                  </Button>
                </Space>
              </Col>
            </Row>

            <Table
              columns={replacementColumns}
              dataSource={replacements}
              rowKey="id"
              loading={replacementsLoading}
              onRow={(record) => ({
                onClick: () => openReplacementDrawer(record),
                style: { cursor: 'pointer' },
              })}
              pagination={{
                current: replacementsPagination.current,
                pageSize: replacementsPagination.pageSize,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
                onChange: (page, pageSize) => {
                  setReplacementsPagination({ current: page, pageSize });
                },
              }}
            />
          </Card>
        </TabPane>
        )}
        <TabPane tab="Equipment" key="equipment">
          <Card>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
              <Col>
                <Input
                  placeholder="Search equipment..."
                  prefix={<SearchOutlined />}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: 300 }}
                />
              </Col>
              {canManageReplacements && (
                <Col>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
                    Add Equipment
                  </Button>
                </Col>
              )}
            </Row>

            <Table
              columns={equipmentColumns}
              dataSource={equipment}
              rowKey="id"
              loading={isLoading}
              pagination={{
                current: equipmentPagination.current,
                pageSize: equipmentPagination.pageSize,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
                onChange: (page, pageSize) => {
                  setEquipmentPagination({ current: page, pageSize });
                },
              }}
            />
          </Card>
        </TabPane>

        <TabPane tab="Equipment Types" key="types">
          <Card>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
              <Col>
                <Title level={4}>Equipment Types</Title>
              </Col>
              {isAdmin && (
                <Col>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setTypeModalVisible(true)}>
                    Add Type
                  </Button>
                </Col>
              )}
            </Row>

            <Table
              columns={typeColumns}
              dataSource={equipmentTypes}
              rowKey="value"
              loading={typesLoading}
              pagination={false}
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* Equipment Modal */}
      <Modal
        title={editingId ? 'Edit Equipment' : 'Add Equipment'}
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="equipment_type"
            label="Equipment Type"
            rules={[{ required: true, message: 'Please select equipment type' }]}
          >
            <Select placeholder="Select equipment type">
              {equipmentTypes.map((type: EquipmentType) => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="model"
            label="Model"
            rules={[{ required: true, message: 'Please enter model' }]}
          >
            <Input placeholder="Enter model" />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <Select placeholder="Select status">
              <Option value="active">Active</Option>
              <Option value="not_supported">Not Supported</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="location"
            label="Location"
          >
            <Input placeholder="Enter location" />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes"
          >
            <Input.TextArea rows={3} placeholder="Enter notes" />
          </Form.Item>

          <Form.Item>
            <Space>
          <Button 
                type="primary"
                htmlType="submit"
                loading={createEquipmentMutation.isLoading || updateEquipmentMutation.isLoading}
              >
                {editingId ? 'Update' : 'Create'}
              </Button>
              <Button onClick={handleModalClose}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Equipment Type Modal */}
      <Modal
        title="Add Equipment Type"
        open={typeModalVisible}
        onCancel={handleTypeModalClose}
        footer={null}
        width={400}
      >
        <Form
          form={typeForm}
          layout="vertical"
          onFinish={handleTypeSubmit}
        >
          <Form.Item
            name="value"
            label="Value"
            rules={[{ required: true, message: 'Please enter value' }]}
          >
            <Input placeholder="Enter value (e.g., pos_terminal)" />
          </Form.Item>

          <Form.Item
            name="label"
            label="Label"
            rules={[{ required: true, message: 'Please enter label' }]}
          >
            <Input placeholder="Enter label (e.g., POS Terminal)" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createTypeMutation.isLoading}
              >
                Create
              </Button>
              <Button onClick={handleTypeModalClose}>
                Cancel
          </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Replacement Modal - can minimize to grab ticket # then restore */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginRight: 24 }}>
            <span>{editingReplacementId ? 'Edit Replacement' : 'Add Replacement'}</span>
            <Space size="small">
              <Tooltip title="Minimize">
                <Button type="text" size="small" icon={<MinusOutlined />} onClick={handleReplacementMinimize} />
              </Tooltip>
              <Tooltip title="Close">
                <Button type="text" size="small" icon={<CloseOutlined />} onClick={handleReplacementModalClose} />
              </Tooltip>
            </Space>
          </div>
        }
        closeIcon={null}
        open={replacementModalVisible && !replacementModalMinimized}
        onCancel={handleReplacementModalClose}
        footer={null}
        width={600}
      >
        <Form
          form={replacementForm}
          layout="vertical"
          onFinish={handleReplacementSubmit}
        >
          <Form.Item
            name="business_id"
            label="Business"
            rules={[{ required: true, message: 'Please select a business' }]}
          >
            <Select
              showSearch
              placeholder="Search by business name, MID or store ID..."
              onSearch={(value) => setBusinessSearchTerm(value)}
              filterOption={false}
              loading={businessesLoading || businessSearchPending}
              notFoundContent={
                businessSearchTerm.trim().length < 2
                  ? <div>Type at least 2 characters to search...</div>
                  : (businessesLoading || businessSearchPending)
                    ? <Spin size="small" />
                    : <Empty description="No businesses found" />
              }
              style={{ width: '100%' }}
              dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
              optionLabelProp="label"
              allowClear
            >
              {allBusinesses.map((business: Business) => {
                const addressParts = [];
                if (business.address) addressParts.push(business.address);
                if (business.city) addressParts.push(business.city);
                if (business.state) addressParts.push(business.state);
                if (business.zip_code) addressParts.push(business.zip_code);
                const fullAddress = addressParts.length > 0 ? ` - ${addressParts.join(', ')}` : '';
                const displayText = `${business.business_name}${business.mid ? ` (${business.mid})` : ''}${fullAddress}`;
                return (
                  <Option key={business.id} value={business.id} label={business.business_name} title={displayText}>
                    <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }} title={displayText}>
                      <div style={{ fontWeight: 500 }}>{business.business_name}</div>
                      {business.mid && <div style={{ fontSize: '12px', color: '#666' }}>MID: {business.mid}</div>}
                      {fullAddress && <div style={{ fontSize: '12px', color: '#999' }}>{addressParts.join(', ')}</div>}
                    </div>
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item
            name="equipment_type"
            label="Equipment Type"
            rules={[{ required: true, message: 'Please select equipment type' }]}
          >
            <Select 
              placeholder="Select equipment type"
              onChange={(value) => {
                // Clear model when equipment type changes and update state
                setSelectedEquipmentType(value);
                replacementForm.setFieldsValue({ model: undefined, equipment_type: value });
              }}
            >
              {equipmentTypes.map((type: EquipmentType) => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="model"
            label="Model"
            rules={[{ required: true, message: 'Please select model' }]}
          >
            <Select 
              placeholder={selectedEquipmentType ? (equipmentModels.length === 0 ? "No models found for this type" : "Select model") : "Select equipment type first"}
              disabled={!selectedEquipmentType}
              loading={modelsLoading}
              showSearch
              filterOption={(input, option) => {
                const value = String(option?.value || '');
                return value.toLowerCase().includes(input.toLowerCase());
              }}
              notFoundContent={modelsLoading ? <Spin size="small" /> : <Empty description="No models found" />}
            >
              {(equipmentModels as string[]).map((model: string) => (
                <Option key={model} value={model}>
                  {model}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="ticket_id"
            label={editingReplacementId ? 'Ticket Number' : 'Support Ticket (optional)'}
            extra={!editingReplacementId ? 'Link to an existing ticket or leave blank to create a new RP ticket automatically.' : undefined}
          >
            <Select
              showSearch
              placeholder="Search by ticket # or subject..."
              value={replacementForm.getFieldValue('ticket_id')}
              onSearch={(value) => setTicketSearchTerm(value)}
              filterOption={false}
              notFoundContent={ticketSearchTerm.length < 1 ? <div>Type to search tickets...</div> : (replacementTicketsLoading ? <Spin size="small" /> : <Empty description="No tickets found" />)}
              style={{ width: '100%' }}
              dropdownStyle={{ maxHeight: 300, overflow: 'auto' }}
              allowClear
              optionLabelProp="label"
            >
              {replacementTicketsOptions.map((t: any) => (
                <Option key={t.id} value={t.id} label={`#${t.ticket_number}`} title={t.subject}>
                  <div style={{ whiteSpace: 'normal' }}>
                    <span style={{ fontWeight: 500 }}>#{t.ticket_number}</span>
                    {t.subject && <span style={{ color: '#666', marginLeft: 8 }}>{t.subject.length > 50 ? t.subject.slice(0, 50) + '…' : t.subject}</span>}
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="replaced_by_user_id"
            label="Who Replaced It"
            rules={[{ required: true, message: 'Please select who replaced it' }]}
          >
            <Select
              showSearch
              placeholder="Search and select a user..."
              filterOption={(input, option) => {
                const label = (option?.label ?? '') as string;
                return label.toLowerCase().includes(input.toLowerCase());
              }}
              optionFilterProp="label"
              style={{ width: '100%' }}
              dropdownStyle={{ maxHeight: 300, overflow: 'auto' }}
              allowClear
              optionLabelProp="label"
              options={agentsList.map((a: any) => ({
                value: a.id,
                label: `${a.first_name} ${a.last_name}${a.role ? ` (${a.role})` : ''}`,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select status' }]}
            initialValue="new"
          >
            <Select>
              {REPLACEMENT_STATUS_OPTIONS.map((s) => (
                <Option key={s.value} value={s.value}>{s.label}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="replacement_date"
            label="Date of Replacement"
            rules={[{ required: true, message: 'Please select replacement date' }]}
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item
            name="serial_initial"
            label="Serial: Initial device"
          >
            <Input placeholder="Initial device serial number" />
          </Form.Item>

          <Form.Item
            name="serial_number"
            label="Serial: Replacement device"
          >
            <Input placeholder="Replacement device serial number" />
          </Form.Item>

          <Form.Item
            name="tracking_number"
            label="Tracking number"
            extra="Carrier is auto-detected when possible. Use Track shipment after create for the carrier page."
          >
            <Input placeholder="Tracking number" />
          </Form.Item>

          <Form.Item shouldUpdate={(prev, cur) => prev.tracking_number !== cur.tracking_number} noStyle>
            {() => {
              const tn = replacementForm.getFieldValue('tracking_number');
              if (!tn) return null;
              return (
                <div style={{ marginBottom: 16 }}>
                  {renderTrackingPanel(tn)}
                </div>
              );
            }}
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes"
          >
            <Input.TextArea rows={3} placeholder="Enter any additional notes" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createReplacementMutation.isLoading || updateReplacementMutation.isLoading}
              >
                {editingReplacementId ? 'Update' : 'Create'}
              </Button>
              <Button onClick={handleReplacementModalClose}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={dashboardReplacementsModal ? `${dashboardReplacementsModal.model} — Replacements` : 'Replacements'}
        open={!!dashboardReplacementsModal?.visible}
        onCancel={() => setDashboardReplacementsModal(null)}
        footer={null}
        width={1000}
      >
        <Table
          columns={replacementColumns.filter((col) => col.key !== 'actions')}
          dataSource={dashboardReplacements}
          rowKey="id"
          loading={dashboardReplacementsLoading}
          onRow={(record) => ({
            onClick: () => {
              setDashboardReplacementsModal(null);
              openReplacementDrawer(record);
            },
            style: { cursor: 'pointer' },
          })}
          pagination={{
            current: dashboardReplacementsPagination.current,
            pageSize: dashboardReplacementsPagination.pageSize,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            onChange: (page, pageSize) => {
              setDashboardReplacementsPagination({ current: page, pageSize });
            },
          }}
          size="small"
        />
      </Modal>

      <Drawer
        title={
          selectedReplacement ? (
            <div>
              <div>
                {selectedReplacement.equipment_type} – {selectedReplacement.model}
              </div>
              <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
                {selectedReplacement.business_name}
              </Text>
            </div>
          ) : 'Replacement'
        }
        open={commentsDrawerVisible}
        onClose={handleCloseReplacementDrawer}
        width={920}
        styles={{ body: { display: 'flex', flexDirection: 'column', padding: 0, background: '#FEFBFF', overflow: 'hidden' } }}
      >
        {selectedReplacement && (
          <div style={{ display: 'flex', flex: 1, minHeight: 0, height: '100%' }}>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                borderRight: '1px solid #E7E0EC',
                background: '#fff',
              }}
            >
              <div style={{ padding: workspaceSectionPadding, borderBottom: '1px solid #E7E0EC', backgroundColor: '#FEFBFF', flexShrink: 0 }}>
                <Text strong style={{ display: 'block', marginBottom: 10, fontSize: 13 }}>
                  Add a comment
                </Text>
                <input
                  ref={commentFileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleCommentFileSelect}
                />
                <div
                  style={{
                    border: '1px solid #E7E0EC',
                    borderRadius: 8,
                    backgroundColor: '#fff',
                    overflow: 'hidden',
                  }}
                >
                  {renderSelectedFileTags(commentSelectedFiles, (index) =>
                    setCommentSelectedFiles((prev) => prev.filter((_, i) => i !== index))
                  )}
                  <TextArea
                    rows={4}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Type your comment..."
                    bordered={false}
                    style={{
                      resize: 'none',
                      minHeight: 120,
                      padding: '12px 14px',
                      fontSize: 14,
                      lineHeight: 1.6,
                      background: 'transparent',
                    }}
                    onPressEnter={(e) => {
                      if (!e.shiftKey) {
                        e.preventDefault();
                        handleSendComment();
                      }
                    }}
                  />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 12px',
                      borderTop: '1px solid #E7E0EC',
                      background: '#fafafa',
                    }}
                  >
                    <Button icon={<PaperClipOutlined />} onClick={() => commentFileInputRef.current?.click()}>
                      Attach
                    </Button>
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      onClick={handleSendComment}
                      loading={addCommentMutation.isLoading || uploadingFiles}
                      disabled={!newComment.trim() && commentSelectedFiles.length === 0}
                      style={{ borderRadius: 8 }}
                    >
                      {uploadingFiles ? 'Uploading...' : 'Send'}
                    </Button>
                  </div>
                </div>
              </div>

              <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: workspaceSectionPadding }}>
                {commentsLoading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <Spin size="large" />
                  </div>
                ) : comments.length === 0 ? (
                  <Text type="secondary">No comments yet.</Text>
                ) : (
                  <List
                    dataSource={[...comments].reverse()}
                    style={{ width: '100%' }}
                    locale={{ emptyText: '' }}
                    renderItem={(item) => {
                      const authorFirst = item.first_name || 'U';
                      const authorLast = item.last_name || '';
                      return (
                        <List.Item style={{ marginBottom: 12, alignItems: 'flex-start', padding: 0, width: '100%' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 10 }}>
                            <Avatar style={{ backgroundColor: '#1890ff', flexShrink: 0, width: 40, height: 40 }}>
                              {authorFirst[0] || 'U'}
                            </Avatar>
                            <div
                              style={{
                                flex: 1,
                                minWidth: 0,
                                border: '1px solid #d9d9d9',
                                backgroundColor: '#ffffff',
                                borderRadius: 8,
                                padding: '10px 14px',
                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                <Text style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                  {authorFirst} {authorLast}
                                </Text>
                                <Space size="small">
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    {dayjs(item.created_at).format('MMM D, YYYY h:mm A')}
                                  </Text>
                                  {isAdmin && (
                                    <Popconfirm
                                      title="Delete this comment?"
                                      onConfirm={() => deleteCommentMutation.mutate(item.id)}
                                    >
                                      <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                                    </Popconfirm>
                                  )}
                                </Space>
                              </div>
                              {item.comment ? (
                                <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem', lineHeight: 1.6, color: '#262626' }}>
                                  {item.comment}
                                </div>
                              ) : (
                                <Text type="secondary" style={{ fontStyle: 'italic', fontSize: '0.875rem' }}>
                                  No content
                                </Text>
                              )}
                              {renderAttachmentTags(item.attachments || [], Boolean(item.comment))}
                            </div>
                          </div>
                        </List.Item>
                      );
                    }}
                  />
                )}
              </div>
            </div>

            <div
              style={{
                width: 340,
                flexShrink: 0,
                overflowY: 'auto',
                background: '#fff',
              }}
            >
              <div style={{ padding: workspaceSectionPadding, borderBottom: '1px solid #E7E0EC' }}>
                <Title level={5} style={{ fontWeight: 600, margin: '0 0 12px 0' }}>
                  Replacement Details
                </Title>
                <Descriptions size="small" column={1}>
                  <Descriptions.Item label="Equipment Type">
                    {selectedReplacement.equipment_type}
                  </Descriptions.Item>
                  <Descriptions.Item label="Model">{selectedReplacement.model}</Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Select
                      size="small"
                      value={selectedReplacement.status || 'new'}
                      onChange={(status) => handleQuickStatusChange(selectedReplacement, status)}
                      loading={
                        updateReplacementStatusMutation.isLoading
                        && updateReplacementStatusMutation.variables?.id === selectedReplacement.id
                      }
                      style={{ minWidth: 130 }}
                    >
                      {REPLACEMENT_STATUS_OPTIONS.map((status) => (
                        <Option key={status.value} value={status.value}>
                          <span style={{ color: status.textColor, fontWeight: 500 }}>
                            {status.label}
                          </span>
                        </Option>
                      ))}
                    </Select>
                  </Descriptions.Item>
                  <Descriptions.Item label="Who Replaced It">
                    {formatUserName(
                      selectedReplacement.replaced_by_first_name,
                      selectedReplacement.replaced_by_last_name
                    ) !== '—'
                      ? formatUserName(
                          selectedReplacement.replaced_by_first_name,
                          selectedReplacement.replaced_by_last_name
                        )
                      : selectedReplacement.replaced_by || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Date">
                    {dayjs(selectedReplacement.replacement_date).format('MMM D, YYYY')}
                  </Descriptions.Item>
                  <Descriptions.Item label="Serial (Initial)">
                    {selectedReplacement.serial_initial || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Serial (Replacement)">
                    {selectedReplacement.serial_number || '—'}
                  </Descriptions.Item>
                  {selectedReplacement.ticket_number && (
                    <Descriptions.Item label="Ticket">
                      <a
                        href={`/workspace/number/${selectedReplacement.ticket_number}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/workspace/number/${selectedReplacement.ticket_number}`, {
                            state: { openInNewTab: true },
                          });
                        }}
                      >
                        #{selectedReplacement.ticket_number}
                      </a>
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Created By">
                    {formatUserName(
                      selectedReplacement.created_by_first_name,
                      selectedReplacement.created_by_last_name
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created">
                    {dayjs(selectedReplacement.created_at).format('MMM D, YYYY h:mm A')}
                  </Descriptions.Item>
                  {selectedReplacement.notes && (
                    <Descriptions.Item label="Notes">
                      <div style={{ whiteSpace: 'pre-wrap' }}>{selectedReplacement.notes}</div>
                    </Descriptions.Item>
                  )}
                </Descriptions>
                <Space wrap style={{ marginTop: 12 }}>
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEditReplacement(selectedReplacement)}
                  >
                    Edit
                  </Button>
                  {(selectedReplacement.status === 'new' || selectedReplacement.status === 'in_progress') && (
                    <Button
                      size="small"
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      loading={
                        updateReplacementStatusMutation.isLoading
                        && updateReplacementStatusMutation.variables?.id === selectedReplacement.id
                      }
                      onClick={() => handleQuickStatusChange(selectedReplacement, 'completed')}
                    >
                      Mark Solved
                    </Button>
                  )}
                  {selectedReplacement.status === 'completed' && (
                    <Button
                      size="small"
                      icon={<SyncOutlined />}
                      loading={
                        updateReplacementStatusMutation.isLoading
                        && updateReplacementStatusMutation.variables?.id === selectedReplacement.id
                      }
                      onClick={() => handleQuickStatusChange(selectedReplacement, 'in_progress')}
                    >
                      Reopen
                    </Button>
                  )}
                  <Button
                    size="small"
                    icon={<MailOutlined />}
                    onClick={() => handleOpenSendEmail(selectedReplacement)}
                  >
                    Send Email
                  </Button>
                </Space>
              </div>

              <div style={{ padding: workspaceSectionPadding, borderBottom: '1px solid #E7E0EC' }}>
                <Title level={5} style={{ fontWeight: 600, margin: '0 0 12px 0' }}>
                  Business Information
                </Title>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <Avatar style={{ backgroundColor: '#1890ff' }}>
                    {selectedReplacement.business_name?.[0] || 'B'}
                  </Avatar>
                  <div>
                    <Text strong style={{ display: 'block' }}>{selectedReplacement.business_name || '—'}</Text>
                    {formatBusinessAddress(selectedReplacement) && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatBusinessAddress(selectedReplacement)}
                      </Text>
                    )}
                  </div>
                </div>
                <Descriptions size="small" column={1}>
                  {selectedReplacement.mid && (
                    <Descriptions.Item label="MID">{selectedReplacement.mid}</Descriptions.Item>
                  )}
                  {selectedReplacement.business_email && (
                    <Descriptions.Item label="Email">{selectedReplacement.business_email}</Descriptions.Item>
                  )}
                  {selectedReplacement.business_phone && (
                    <Descriptions.Item label="Phone">{selectedReplacement.business_phone}</Descriptions.Item>
                  )}
                </Descriptions>
              </div>

              <div style={{ padding: workspaceSectionPadding, borderBottom: '1px solid #E7E0EC' }}>
                <Title level={5} style={{ fontWeight: 600, margin: '0 0 12px 0' }}>
                  Tracking
                </Title>
                {renderTrackingPanel(selectedReplacement.tracking_number)}
              </div>

              {taskAttachments.length > 0 && (
                <div style={{ padding: workspaceSectionPadding, borderBottom: '1px solid #E7E0EC' }}>
                  <Title level={5} style={{ fontWeight: 600, margin: '0 0 12px 0' }}>
                    Attachments
                  </Title>
                  {renderAttachmentTags(taskAttachments, false)}
                </div>
              )}

              <div style={{ padding: workspaceSectionPadding }}>
                <Title level={5} style={{ fontWeight: 600, margin: '0 0 12px 0' }}>
                  Activity
                </Title>
                {activityLoading ? (
                  <Spin size="small" />
                ) : activityEvents.length === 0 ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>No activity recorded yet.</Text>
                ) : (
                  <Timeline items={buildActivityTimelineItems(activityEvents)} />
                )}
              </div>
            </div>
          </div>
        )}
      </Drawer>

      <Modal
        title={previewAttachment?.original_filename || 'Attachment preview'}
        open={!!previewAttachment}
        onCancel={closeAttachmentPreview}
        footer={null}
        width={previewAttachment?.mime_type?.startsWith('image/') ? 720 : 900}
        destroyOnClose
      >
        {previewLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : previewUrl && previewAttachment ? (
          previewAttachment.mime_type?.startsWith('image/') ? (
            <img
              src={previewUrl}
              alt={previewAttachment.original_filename}
              style={{ maxWidth: '100%' }}
            />
          ) : (
            <iframe
              title={previewAttachment.original_filename}
              src={previewUrl}
              style={{ width: '100%', height: 700, border: 'none' }}
            />
          )
        ) : null}
      </Modal>

      <Modal
        title="Send Email"
        open={emailModalVisible}
        onCancel={handleCloseEmailModal}
        okText="Send Email"
        okButtonProps={{
          icon: <MailOutlined />,
          disabled: !emailPreview?.to || emailPreviewLoading || !emailMessage.trim(),
          loading: sendReplacementEmailMutation.isLoading,
        }}
        onOk={() => {
          if (!emailReplacementForSend) return;
          const trimmed = emailMessage.trim();
          if (!trimmed) {
            message.warning('Please type a message to include in the email');
            return;
          }
          sendReplacementEmailMutation.mutate({
            replacementId: emailReplacementForSend.id,
            message: trimmed,
          });
        }}
        destroyOnClose
      >
        {emailPreviewLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Text type="secondary">Business</Text>
              <div>
                <Text strong>
                  {emailPreview?.business_name || emailReplacementForSend?.business_name || '—'}
                </Text>
              </div>
            </div>
            <div>
              <Text type="secondary">Recipient</Text>
              <div><Text>{emailPreview?.to || '—'}</Text></div>
            </div>
            <div>
              <Text type="secondary">Subject</Text>
              <div><Text>{emailPreview?.subject || '—'}</Text></div>
            </div>
            <div>
              <Text type="secondary">Message</Text>
              <TextArea
                rows={5}
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Type the message to include in this email"
                maxLength={5000}
                style={{ marginTop: 6 }}
              />
            </div>
            {!emailPreview?.to && (
              <Alert
                type="warning"
                showIcon
                message="This business does not have an email address on file. Add an email to the business record before sending."
              />
            )}
          </Space>
        )}
      </Modal>

      {/* Minimized replacement bar - click Restore to bring modal back */}
      {replacementModalVisible && replacementModalMinimized && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            background: '#1890ff',
            color: '#fff',
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          <span style={{ fontWeight: 500 }}>
            {editingReplacementId ? 'Edit Replacement' : 'Add Replacement'} (minimized)
          </span>
          <Button type="primary" size="small" ghost onClick={handleReplacementRestore} style={{ color: '#fff', borderColor: '#fff' }}>
            Restore
          </Button>
          <Button type="text" size="small" onClick={handleReplacementModalClose} style={{ color: 'rgba(255,255,255,0.85)' }}>
            Close
          </Button>
        </div>
      )}
    </div>
  );
};

export default Equipment;